import { ChevronDown, ChevronLeft } from "lucide-react";
import { FC, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Outlet, useNavigate } from "react-router-dom";
import { Button } from "~/components/ui/button";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "~/components/ui/drawer";
import { ROUTES, useSettingsParams } from "~/helpers";
import { useMobile } from "~/hooks/use-mobile";
import { trackEvent } from "~/utils/analytics";
import { NavMenu } from "./NavMenu";
import { SettingsMenuItem } from "./types";

const settingsMenuLabels: Record<SettingsMenuItem, string> = {
  account: "Account",
  appearance: "Appearance",
  organization: "Organization",
  team: "Team",
  categoriesManagement: "Categories",
  knowledge: "Knowledge",
  reports: "Reports",
  planAndUsage: "Billing",
  analytics: "Analytics",
  developers: "Developers",
  archived: "Archive",
  deletedDocuments: "Deleted Documents",
};

export const SettingsLayout: FC = () => {
  const navigate = useNavigate();
  const { organizationId, settingsItem } = useSettingsParams();
  const { isMobile, isLoading: isMobileLoading } = useMobile();
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const handleMenuItemClick = (settingsItem: SettingsMenuItem) => {
    navigate(ROUTES.SETTINGS_ITEM({ organizationId, settingsItem }));
    setMobileNavOpen(false);
  };

  useEffect(() => {
    if (!settingsItem) {
      navigate(
        ROUTES.SETTINGS_ITEM({ organizationId, settingsItem: "account" })
      );
    }
  }, [settingsItem, organizationId, navigate]);

  // Prevent layout flash during hydration - must be after all hooks
  if (isMobileLoading) {
    return null;
  }

  const allMenuItems: SettingsMenuItem[] = [
    "account",
    "appearance",
    "organization",
    "team",
    "categoriesManagement",
    "knowledge",
    "reports",
    "planAndUsage",
    "analytics",
    "developers",
    "archived",
  ];

  const availableMenuItems = allMenuItems;

  const handleBack = () => {
    trackEvent({ eventName: "app/settings_account/cases.button/click" });
    navigate("..");
  };

  const currentLabel =
    (settingsItem && settingsMenuLabels[settingsItem]) || "Settings";

  return (
    <div className="p-4 sm:p-6">
      <div className="flex items-start mb-4 sm:mb-6">
        <div className="flex gap-4 items-center">
          <Button variant="secondary" size="icon" onClick={handleBack}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h3 className="text-xl sm:text-2xl font-bold">
            <FormattedMessage id="settings" defaultMessage={"Settings"} />
          </h3>
        </div>
      </div>

      {isMobile ? (
        <div className="flex flex-col gap-4">
          <Drawer open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
            <DrawerTrigger asChild>
              <Button variant="outline" className="w-full justify-between h-12">
                <span>{currentLabel}</span>
                <ChevronDown className="h-4 w-4" />
              </Button>
            </DrawerTrigger>
            <DrawerContent className="pb-safe-bottom">
              <DrawerHeader>
                <DrawerTitle>
                  <FormattedMessage
                    id="settings.navigation"
                    defaultMessage="Settings Navigation"
                  />
                </DrawerTitle>
              </DrawerHeader>
              <div className="px-4 pb-6">
                <NavMenu
                  activeItem={settingsItem}
                  onItemClick={handleMenuItemClick}
                  availableMenuItems={availableMenuItems}
                />
              </div>
            </DrawerContent>
          </Drawer>
          <Outlet />
        </div>
      ) : (
        <div className="flex gap-4 items-start">
          <NavMenu
            activeItem={settingsItem}
            onItemClick={handleMenuItemClick}
            availableMenuItems={availableMenuItems}
          />
          <Outlet />
        </div>
      )}
    </div>
  );
};
